import {
	TRANSACTION_TYPE_BULK_FORM_FIELD,
	TransactionTypeBulkFormDto,
	useTransactionTypeBulkDrawerController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/TransactionTypeBulkDrawer/useTransactionTypeBulkDrawerController.ts';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { getExpectedTransactionTypeOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/expectedTransactionType.utils.ts';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { ExpectedTransactionType } from '@dealstack/shared/lib/generated';
import { Button, Flex } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';

interface Props {
	opened: boolean;
	onClose: () => void;
	loading?: boolean;
	updateTransactionType: (expectedTransactionType: ExpectedTransactionType | null) => void;
}

export const TransactionTypeBulkDrawer = ({
	opened,
	onClose,
	loading,
	updateTransactionType,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { transactionTypeBulkForm } = useTransactionTypeBulkDrawerController(opened);

	const submitBulkUpdate = (formValues: TransactionTypeBulkFormDto) => {
		updateTransactionType(formValues.expectedTransactionType);
	};

	return (
		<GenericDrawer
			opened={opened}
			onClose={onClose}
			transitionProps={drawerTransitionProps}
			position="right"
			title={tMipManage('transaction_type_bulk_drawer.title')}
			body={
				<Flex direction="column" h="100%" gap="2rem">
					<Flex direction="column" gap=".5rem">
						<InfoBox
							iconVariant="information"
							rippleColor="orange"
							alignContent="center"
							description={tMipManage(
								'transaction_type_bulk_drawer.applies_to_all_info'
							)}
						/>
						<InfoBox
							iconVariant="information"
							rippleColor="blue"
							alignContent="center"
							description={tMipManage(
								'transaction_type_bulk_drawer.released_excluded_info'
							)}
						/>
					</Flex>

					<FormProvider {...transactionTypeBulkForm}>
						<form
							onSubmit={transactionTypeBulkForm.handleSubmit(submitBulkUpdate)}
							style={{ height: '100%' }}
						>
							<Flex direction="column" gap="1rem" h="100%" justify="space-between">
								<Select
									name={TRANSACTION_TYPE_BULK_FORM_FIELD.expectedTransactionType}
									control={transactionTypeBulkForm.control}
									size="md"
									label={tMipManage(
										'investment_allocations.drawer.transaction_type_label'
									)}
									placeholder={tMipManage(
										'investment_allocations.drawer.transaction_type_placeholder'
									)}
									data={getExpectedTransactionTypeOptions(tCapManage)}
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{ style: { pointerEvents: 'none' } }}
									clearable
								/>

								<Flex direction="column" gap="0.5rem" mt="2rem">
									<Button type="submit" fullWidth mt="auto" loading={loading}>
										{tCommonUi('buttons.update')}
									</Button>
									<Button onClick={onClose} fullWidth variant="default">
										{tCommonUi('buttons.cancel')}
									</Button>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</Flex>
			}
		/>
	);
};
